import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Pencil, Trash2, Plus, X, Save, Loader2, Users, Search, EyeOff, Eye, GripVertical,
  ArrowUpDown, LayoutGrid, List, MapPin, Mail, Phone, Linkedin, FileText, Languages,
  Briefcase, Calendar as CalendarIcon, ChevronDown, Star, Camera, AlertCircle, CheckCircle2,
  UserPlus, Send, Download, Mic, Globe, ArrowRight, UserCheck, UserX,
} from 'lucide-react';
import { api, formatApiError, absoluteUrl } from '../../lib/api';
import { fuzzyMatch } from '../../lib/textSearch';
import ChipInput from '../../components/ChipInput';
import ChipMultiSelect from '../../components/ChipMultiSelect';
import ImageUploader from '../../components/ImageUploader';
import RichTextEditor from '../../components/RichTextEditor';
import CertificationsField from '../../components/CertificationsField';
import InterpreterProfilePreview from '../../components/InterpreterProfilePreview';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

// Derives directional language combinations following AIIC conventions:
//   • active × active = both directions
//   • passive → active = one direction only
// Returns a de-duplicated list of [from, to] pairs (self-pairs dropped).
function deriveLanguagePairs(active, passive) {
  const A = (active || []).filter(Boolean);
  const P = (passive || []).filter(Boolean);
  const out = [];
  for (let i = 0; i < A.length; i += 1) {
    for (let j = 0; j < A.length; j += 1) {
      if (i !== j) out.push([A[i], A[j]]);
    }
  }
  for (const p of P) for (const a of A) out.push([p, a]);
  const seen = new Set();
  return out.filter(([from, to]) => {
    if (!from || !to || from === to) return false;
    const k = `${from}|${to}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
const pairKey = (from, to) => `${from}→${to}`;

const empty = {
  full_name: '',
  nombres: '', apellido_paterno: '', apellido_materno: '',
  languages: '', city: '', email: '', phone: '',
  specialty: '', bio: '', linkedin_url: '', website: '', language_pairs: '',
  references_text: '', photo_url: '', is_active: true,
  active_languages: [], passive_languages: [],
  modalities: [],
  additional_emails: [], additional_phones: [],
  excluded_language_pairs: [],
  paid_until: '', membership_notes: '',
  membership_status: 'active',
  data_verified: false,
};

// ───────────────────────────────────────────────────────────────────────────
// Page-level component
// ───────────────────────────────────────────────────────────────────────────
export default function AdminInterpretersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('cards'); // cards | table
  const [reorderMode, setReorderMode] = useState(false);
  const [orderDraft, setOrderDraft] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const dragSlugRef = useRef(null);
  // Invitation selection mode — adds checkbox overlays on each card.
  const [inviteMode, setInviteMode] = useState(false);
  const [inviteSelected, setInviteSelected] = useState(() => new Set());
  function toggleInviteSelect(slug) {
    setInviteSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  }

  // CSV export: triggers a browser download of the full interpreters list,
  // ordered alphabetically by surname. Used by the admin to audit and clean
  // the data offline (then re-imports via bulk CSV upload).
  const [exporting, setExporting] = useState(false);
  async function downloadCsv() {
    setExporting(true);
    try {
      const res = await api.get('/admin/interpreters/export', { responseType: 'blob' });
      // Parse the filename out of the Content-Disposition header (fallback to a sensible default)
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || `cmic-interpretes-${new Date().toISOString().slice(0, 10)}.csv`;
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || 'No se pudo exportar el CSV');
    } finally {
      setExporting(false);
    }
  }

  // ── Data loading ────────────────────────────────────────────────────────
  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/interpreters');
      setItems(data || []);
    } catch {
      try {
        const { data } = await api.get('/interpreters');
        setItems(data || []);
      } catch { /* noop */ }
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  // Send a single invitation straight from the interpreter profile/list.
  async function sendSingleInvite(it) {
    if (!it || it.has_account || !(it.email || '').trim()) return;
    if (!window.confirm(`¿Enviar invitación por correo a ${it.full_name} (${it.email})?\n\nRecibirá un enlace para crear su cuenta.`)) return;
    try {
      const { data } = await api.post('/admin/interpreters/bulk-invite', { slugs: [it.slug], invite_all_with_email: false });
      const sent = data?.summary?.sent || 0;
      if (sent > 0) window.alert(`✅ Invitación enviada a ${it.email}.`);
      else window.alert(`No se envió la invitación (${data?.results?.[0]?.status || 'revisa el correo del intérprete'}).`);
      await refresh();
    } catch {
      window.alert('No se pudo enviar la invitación. Inténtalo de nuevo.');
    }
  }

  // Deep-link: /admin/interpreters?slug=<slug> auto-opens that profile's editor.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const slug = searchParams.get('slug');
    if (!slug || loading || !items.length) return;
    const it = items.find((x) => x.slug === slug);
    if (it) {
      openEdit(it);
      searchParams.delete('slug');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loading]);

  // ── Catalogs (languages / specialties / modalities) ──────────────────────
  // Same source as the bulk editor so the individual editor offers a
  // selectable list instead of free typing only.
  const [catalogs, setCatalogs] = useState({ languages: [], specialties: [], modalities: [] });
  async function loadCatalog(name) {
    try {
      const { data } = await api.get(`/admin/catalogs/${name}`);
      setCatalogs((c) => ({ ...c, [name]: data.values || [] }));
    } catch { /* non-fatal */ }
  }
  async function addToCatalog(name, value) {
    const { data } = await api.post(`/admin/catalogs/${name}`, { value });
    setCatalogs((c) => ({ ...c, [name]: data.values || [] }));
    return data.values;
  }
  useEffect(() => {
    loadCatalog('languages');
    loadCatalog('specialties');
    loadCatalog('modalities');
  }, []);

  // ── CRUD ───────────────────────────────────────────────────────────────
  function openCreate() { setEditing('new'); setForm(empty); setError(''); }
  function openEdit(it) {
    setEditing(it.slug);
    setForm({
      ...empty,
      ...it,
      nombres: it.nombres || '',
      apellido_paterno: it.apellido_paterno || '',
      apellido_materno: it.apellido_materno || '',
      active_languages: Array.isArray(it.active_languages) ? it.active_languages : [],
      passive_languages: Array.isArray(it.passive_languages) ? it.passive_languages : [],
      modalities: Array.isArray(it.modalities) ? it.modalities : [],
      additional_emails: Array.isArray(it.additional_emails) ? it.additional_emails : [],
      additional_phones: Array.isArray(it.additional_phones) ? it.additional_phones : [],
      excluded_language_pairs: Array.isArray(it.excluded_language_pairs) ? it.excluded_language_pairs : [],
      // Specialty is shown as chips now; normalize legacy CSV string → array.
      specialty: Array.isArray(it.specialty)
        ? it.specialty
        : (it.specialty ? String(it.specialty).split(',').map((s) => s.trim()).filter(Boolean) : []),
      // Legacy `languages` may be a list in some rows; the model expects a
      // string, so coerce here to avoid a 422 when saving untouched profiles.
      languages: Array.isArray(it.languages) ? it.languages.join(', ') : (it.languages || ''),
      // Single "Visible en directorio" toggle reflects the TRUE public
      // visibility = active AND listed. Saving syncs both flags together.
      is_active: it.is_active !== false && it.is_listed_in_directory !== false,
    });
    setError('');
  }
  function close() { setEditing(null); setError(''); }

  async function save() {
    setSaving(true); setError('');
    try {
      // Derive full_name automatically from the 3 parts when admin has filled
      // them in. Mexican order: "Nombre(s) Paterno Materno". Falls back to
      // whatever the admin typed in the legacy full_name field.
      const ap = (form.apellido_paterno || '').trim();
      const am = (form.apellido_materno || '').trim();
      const nom = (form.nombres || '').trim();
      const derivedFullName = [nom, ap, am].filter(Boolean).join(' ').trim();
      // Prune excluded pairs that no longer exist in the current derived set
      // (e.g. admin removed a language). Keeps the stored override clean.
      const validKeys = new Set(
        deriveLanguagePairs(form.active_languages, form.passive_languages).map(([f, t]) => pairKey(f, t)),
      );
      const cleanExcluded = (form.excluded_language_pairs || []).filter((k) => validKeys.has(k));
      const payload = {
        ...form,
        full_name: derivedFullName || form.full_name || '',
        active_languages: form.active_languages || [],
        passive_languages: form.passive_languages || [],
        modalities: form.modalities || [],
        excluded_language_pairs: cleanExcluded,
        data_verified: !!form.data_verified,
        is_active: !!form.is_active,
        // Keep public directory visibility in sync with the single toggle.
        is_listed_in_directory: !!form.is_active,
      };
      if (editing === 'new') await api.post('/admin/interpreters', payload);
      else await api.put(`/admin/interpreters/${editing}`, payload);
      await refresh();
      close();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  }

  async function remove(slug) {
    if (!window.confirm('¿Eliminar este intérprete? Esta acción no se puede deshacer.')) return;
    try {
      await api.delete(`/admin/interpreters/${slug}`);
      await refresh();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  }

  async function toggleActive(it) {
    try {
      const currentlyVisible = it.is_active !== false && it.is_listed_in_directory !== false;
      const next = !currentlyVisible;
      await api.put(`/admin/interpreters/${it.slug}/visibility`, {
        is_active: next,
        is_listed_in_directory: next,
      });
      await refresh();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  }

  // ── Reorder mode ───────────────────────────────────────────────────────
  function enterReorder() { setOrderDraft(items.map((it) => it.slug)); setReorderMode(true); }
  function cancelReorder() { setReorderMode(false); setOrderDraft([]); }
  async function saveOrder() {
    setSavingOrder(true);
    try {
      await api.put('/admin/interpreters/reorder', { ids: orderDraft });
      await refresh();
      setReorderMode(false);
      setOrderDraft([]);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setSavingOrder(false); }
  }
  function onDragStart(slug) { dragSlugRef.current = slug; }
  function onDragOver(e) { e.preventDefault(); }
  function onDrop(targetSlug) {
    const src = dragSlugRef.current;
    if (!src || src === targetSlug) return;
    const next = orderDraft.slice();
    const fromIdx = next.indexOf(src);
    const toIdx = next.indexOf(targetSlug);
    if (fromIdx < 0 || toIdx < 0) return;
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, src);
    setOrderDraft(next);
    dragSlugRef.current = null;
  }

  // ── Filter/search ──────────────────────────────────────────────────────
  const visible = useMemo(() => {
    if (reorderMode) {
      const bySlug = Object.fromEntries(items.map((it) => [it.slug, it]));
      return orderDraft.map((s) => bySlug[s]).filter(Boolean);
    }
    const q = search.trim();
    const inDir = (it) => it.is_active && it.is_listed_in_directory !== false;
    return items.filter((it) => {
      if (filterStatus === 'active' && !inDir(it)) return false;
      if (filterStatus === 'inactive' && inDir(it)) return false;
      if (filterStatus === 'no_photo' && it.photo_url) return false;
      if (filterStatus === 'no_bio' && it.bio) return false;
      if (filterStatus === 'with_account' && !it.has_account) return false;
      if (filterStatus === 'without_account' && it.has_account) return false;
      if (q.length < 2) return true;
      // Accent/case/typo-insensitive search across the key fields.
      const asText = (v) => (Array.isArray(v) ? v.join(' ') : (v || ''));
      const haystack = [
        it.full_name, it.nombres, it.apellido_paterno, it.apellido_materno,
        it.email, it.city, asText(it.languages), asText(it.active_languages),
        asText(it.passive_languages), asText(it.specialty), asText(it.modalities),
        asText(it.additional_emails),
      ].filter(Boolean).join(' · ');
      return fuzzyMatch(q, haystack);
    });
  }, [items, search, filterStatus, reorderMode, orderDraft]);

  const stats = useMemo(() => {
    const inDir = items.filter((i) => i.is_active && i.is_listed_in_directory !== false).length;
    return {
      total: items.length,
      visibles: inDir,
      ocultos: items.length - inDir,
      sinFoto: items.filter((i) => !i.photo_url).length,
      sinBio: items.filter((i) => !i.bio).length,
    };
  }, [items]);

  return (
    <div data-testid="admin-interpreters-page" className="space-y-6">
      <Header
        onCreate={openCreate}
        onReorder={enterReorder}
        onCancelReorder={cancelReorder}
        onSaveOrder={saveOrder}
        reorderMode={reorderMode}
        savingOrder={savingOrder}
        onDownloadCsv={downloadCsv}
        exporting={exporting}
      />

      <StatsBar stats={stats} filterStatus={filterStatus} setFilterStatus={setFilterStatus} />

      <IncompleteProfilesBanner items={items} onSelect={openEdit} />

      <BulkInvitePanel
        items={items}
        onDone={refresh}
        inviteMode={inviteMode}
        setInviteMode={setInviteMode}
        selected={inviteSelected}
        setSelected={setInviteSelected}
      />

      <Toolbar
        search={search}
        setSearch={setSearch}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        viewMode={viewMode}
        setViewMode={setViewMode}
        items={items}
        visibleCount={visible.length}
      />

      {reorderMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-center gap-2">
          <GripVertical size={16} /> Arrastra cualquier tarjeta para reordenar. El orden aquí define cómo aparecen en el directorio público.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin text-cmic-500" size={36} />
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
          <Users size={48} className="text-charcoal-300 mx-auto mb-4" />
          <p className="text-charcoal-500">Sin resultados.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <CardGrid
          items={visible}
          onEdit={openEdit}
          onRemove={remove}
          onToggle={toggleActive}
          onInvite={sendSingleInvite}
          reorderMode={reorderMode}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDrop={onDrop}
          inviteMode={inviteMode}
          inviteSelected={inviteSelected}
          onToggleInviteSelect={toggleInviteSelect}
        />
      ) : (
        <TableView
          items={visible}
          onEdit={openEdit}
          onRemove={remove}
          onToggle={toggleActive}
          onInvite={sendSingleInvite}
          inviteMode={inviteMode}
          inviteSelected={inviteSelected}
          onToggleInviteSelect={toggleInviteSelect}
        />
      )}

      {editing && (
        <EditorModal
          editing={editing}
          form={form}
          setForm={setForm}
          save={save}
          close={close}
          saving={saving}
          error={error}
          catalogs={catalogs}
          addToCatalog={addToCatalog}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Sub-components: Header, Stats, Toolbar
// ───────────────────────────────────────────────────────────────────────────
function Header({ onCreate, onReorder, onCancelReorder, onSaveOrder, reorderMode, savingOrder, onDownloadCsv, exporting }) {
  // Reordering disabled by product decision (directory is alphabetical by full_name).
  // The reorder code path is kept intact in case the requirement reverts.
  const enableReorder = false;
  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
          <Users size={26} className="text-cmic-500" /> Intérpretes
        </h1>
        <p className="text-charcoal-500 mt-1 text-sm">Administra los perfiles del directorio público. Se ordenan alfabéticamente por apellido.</p>
      </div>
      <div className="flex items-center gap-2">
        {!reorderMode ? (
          <>
            {enableReorder && (
              <button
                onClick={onReorder}
                data-testid="admin-interpreter-reorder-btn"
                className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                <ArrowUpDown size={16} /> Reordenar
              </button>
            )}
            <button
              onClick={onDownloadCsv}
              disabled={exporting}
              data-testid="admin-interpreter-export-csv-btn"
              title="Descargar todos los intérpretes en formato CSV (útil para auditoría)"
              className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors disabled:opacity-60"
            >
              {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {exporting ? 'Descargando…' : 'Descargar CSV'}
            </button>
            <button
              onClick={onCreate}
              data-testid="admin-interpreter-new-btn"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cmic-500 to-cmic-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:shadow-md hover:shadow-cmic-500/30 transition-all"
            >
              <Plus size={16} /> Nuevo intérprete
            </button>
          </>
        ) : (
          <>
            <button onClick={onCancelReorder} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
              <X size={16} /> Cancelar
            </button>
            <button
              onClick={onSaveOrder}
              disabled={savingOrder}
              data-testid="admin-interpreter-save-order-btn"
              className="inline-flex items-center gap-2 bg-emerald-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-emerald-600 disabled:opacity-60"
            >
              {savingOrder ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar orden
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function StatsBar({ stats, filterStatus, setFilterStatus }) {
  const cards = [
    { label: 'Total', value: stats.total, color: 'cmic', icon: Users, key: 'all' },
    { label: 'Visibles', value: stats.visibles, color: 'emerald', icon: Eye, key: 'active' },
    { label: 'Ocultos', value: stats.ocultos, color: 'charcoal', icon: EyeOff, key: 'inactive' },
    { label: 'Sin foto', value: stats.sinFoto, color: 'amber', icon: Camera, key: 'no_photo' },
    { label: 'Sin biografía', value: stats.sinBio, color: 'rose', icon: FileText, key: 'no_bio' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {cards.map(({ label, value, color, icon: Icon, key }) => {
        const colorClasses = {
          cmic: 'bg-cmic-50 text-cmic-700 border-cmic-100',
          emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
          charcoal: 'bg-gray-50 text-charcoal-700 border-gray-100',
          amber: 'bg-amber-50 text-amber-700 border-amber-100',
          rose: 'bg-rose-50 text-rose-700 border-rose-100',
        }[color];
        const ringClasses = {
          cmic: 'ring-cmic-400', emerald: 'ring-emerald-400', charcoal: 'ring-charcoal-400',
          amber: 'ring-amber-400', rose: 'ring-rose-400',
        }[color];
        const active = filterStatus === key;
        return (
          <button
            key={label}
            type="button"
            onClick={() => setFilterStatus(active && key !== 'all' ? 'all' : key)}
            data-testid={`admin-interpreter-stat-${key}`}
            aria-pressed={active}
            title={key === 'all' ? 'Ver todos' : `Filtrar: ${label}`}
            className={`rounded-xl border ${colorClasses} px-4 py-3 flex items-center gap-3 text-left cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 ${active ? `ring-2 ${ringClasses} shadow-sm` : ''}`}
          >
            <Icon size={20} className="opacity-70 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-2xl font-bold leading-none">{value}</div>
              <div className="text-[11px] uppercase font-medium tracking-wider opacity-80 mt-1 truncate">{label}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Toolbar({ search, setSearch, filterStatus, setFilterStatus, viewMode, setViewMode, items, visibleCount }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-3 flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[240px]">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal-400" />
        <input
          type="text"
          placeholder="Buscar por nombre, ciudad, email, idioma, especialidad…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="admin-interpreter-search"
          className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cmic-300 focus:border-cmic-400 transition-all"
        />
      </div>
      <select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        data-testid="admin-interpreter-filter-status"
        className="border border-gray-200 rounded-xl text-sm px-3 py-2.5 bg-white font-medium"
      >
        <option value="all">Todos ({items.length})</option>
        <option value="active">Visibles ({items.filter((i) => i.is_active && i.is_listed_in_directory !== false).length})</option>
        <option value="inactive">Ocultos ({items.filter((i) => !(i.is_active && i.is_listed_in_directory !== false)).length})</option>
        <option value="no_photo">Sin foto ({items.filter((i) => !i.photo_url).length})</option>
        <option value="no_bio">Sin biografía ({items.filter((i) => !i.bio).length})</option>
        <option value="with_account">Con cuenta ({items.filter((i) => i.has_account).length})</option>
        <option value="without_account">Sin cuenta ({items.filter((i) => !i.has_account).length})</option>
      </select>
      <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
        <button
          onClick={() => setViewMode('cards')}
          data-testid="admin-interpreter-view-cards"
          className={`p-2 rounded-lg transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-cmic-700' : 'text-charcoal-500 hover:text-charcoal-800'}`}
          title="Vista de tarjetas"
        >
          <LayoutGrid size={16} />
        </button>
        <button
          onClick={() => setViewMode('table')}
          data-testid="admin-interpreter-view-table"
          className={`p-2 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-cmic-700' : 'text-charcoal-500 hover:text-charcoal-800'}`}
          title="Vista de tabla"
        >
          <List size={16} />
        </button>
      </div>
      <span className="text-xs text-charcoal-500 ml-auto whitespace-nowrap">
        <span className="font-semibold text-charcoal-700">{visibleCount}</span> mostrados
      </span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// CardGrid: WOW visual list (default)
// ───────────────────────────────────────────────────────────────────────────
function CardGrid({ items, onEdit, onRemove, onToggle, onInvite, reorderMode, onDragStart, onDragOver, onDrop, inviteMode, inviteSelected, onToggleInviteSelect }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {items.map((it) => (
        <InterpreterCard
          key={it.id || it.slug}
          item={it}
          onEdit={() => onEdit(it)}
          onRemove={() => onRemove(it.slug)}
          onToggle={() => onToggle(it)}
          onInvite={() => onInvite(it)}
          reorderMode={reorderMode}
          onDragStart={() => onDragStart(it.slug)}
          onDragOver={onDragOver}
          onDrop={() => onDrop(it.slug)}
          inviteMode={inviteMode}
          isInviteSelected={inviteSelected?.has(it.slug)}
          onToggleInviteSelect={() => onToggleInviteSelect?.(it.slug)}
        />
      ))}
    </div>
  );
}

function InterpreterCard({ item, onEdit, onRemove, onToggle, onInvite, reorderMode, onDragStart, onDragOver, onDrop, inviteMode, isInviteSelected, onToggleInviteSelect }) {
  const it = item;
  const initials = (it.full_name || '?').split(' ').slice(0, 2).map((n) => n.charAt(0)).join('').toUpperCase();
  const hasPhoto = !!it.photo_url;
  const canInvite = inviteMode && (it.email || '').trim() && !it.user_id;
  const inviteDisabled = inviteMode && !canInvite;
  return (
    <div
      data-testid={`admin-interpreter-card-${it.slug}`}
      draggable={reorderMode}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={canInvite ? onToggleInviteSelect : undefined}
      className={`group relative bg-white rounded-2xl border ${isInviteSelected ? 'border-blue-500 ring-2 ring-blue-200' : it.is_active ? 'border-gray-200' : 'border-gray-200 opacity-60'} overflow-hidden hover:shadow-lg hover:shadow-cmic-500/10 ${!inviteMode && 'hover:border-cmic-300 hover:-translate-y-0.5'} transition-all duration-200 ${reorderMode ? 'cursor-move' : (canInvite ? 'cursor-pointer' : (inviteDisabled ? 'cursor-not-allowed opacity-50' : ''))}`}
    >
      {/* Invite-mode checkbox overlay */}
      {inviteMode && (
        <div className="absolute top-2 right-2 z-10">
          {canInvite ? (
            <div
              className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shadow-sm ${isInviteSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'}`}
              data-testid={`card-invite-check-${it.slug}`}
            >
              {isInviteSelected && <CheckCircle2 size={14} className="text-white" />}
            </div>
          ) : (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200" title={it.user_id ? 'Ya tiene cuenta' : 'Sin email'}>
              {it.user_id ? 'tiene cuenta' : 'sin email'}
            </span>
          )}
        </div>
      )}
      {/* Top banner */}
      <div className="relative h-14 bg-gradient-to-br from-cmic-500 via-cmic-600 to-cmic-700">
        {!it.is_active && !inviteMode && (
          <div className="absolute top-2 left-2 bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
            Oculto
          </div>
        )}
        {it.data_verified && !inviteMode && (
          <div
            data-testid={`admin-interpreter-verified-${it.slug}`}
            title="Datos verificados por el admin"
            className="absolute top-2 left-2 inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ring-2 ring-white/40"
            style={{ left: it.is_active ? '0.5rem' : '4.5rem' }}
          >
            <CheckCircle2 size={10} />Verificado
          </div>
        )}
        {reorderMode && (
          <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm text-white p-1 rounded">
            <GripVertical size={14} />
          </div>
        )}
        {!inviteMode && !reorderMode && (
          <div className="absolute top-2 right-2" data-testid={`admin-interpreter-account-${it.slug}`}>
            {it.has_account ? (
              <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ring-2 ring-white/40" title="Tiene cuenta de usuario">
                <UserCheck size={10} /> Con cuenta
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-white/25 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" title="Aún no tiene cuenta de usuario">
                <UserX size={10} /> Sin cuenta
              </span>
            )}
          </div>
        )}
      </div>
      {/* Photo — fully INSIDE the card body (no banner overlap), so it never
          appears "cut". Centered horizontally; circular crop with face anchor. */}
      <div className="px-4 pt-4 flex justify-center">
        {hasPhoto ? (
          <img
            src={absoluteUrl(it.photo_url)}
            alt={it.full_name}
            className="w-24 h-24 rounded-full object-cover object-top border-4 border-white shadow-md bg-gray-100"
          />
        ) : (
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cmic-400 to-cmic-700 text-white flex items-center justify-center text-2xl font-bold border-4 border-white shadow-md">
            {initials}
          </div>
        )}
      </div>
      {/* Content */}
      <div className="px-4 pb-4 pt-3 space-y-2">
        <h3 className="font-bold text-charcoal-800 leading-snug line-clamp-2" title={it.full_name}>
          {it.full_name || '—'}
        </h3>
        {it.city && (
          <div className="flex items-center gap-1.5 text-xs text-charcoal-500">
            <MapPin size={11} className="text-cmic-400 flex-shrink-0" />
            <span className="truncate" title={it.city}>{it.city}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-1 min-h-[24px]">
          {(it.active_languages || []).slice(0, 5).map((l) => (
            <span key={`a-${l}`} className="inline-block text-[10px] font-medium bg-cmic-50 text-cmic-700 px-1.5 py-0.5 rounded border border-cmic-100">{l}</span>
          ))}
          {(it.passive_languages || []).length > (it.active_languages || []).length && (
            <span className="text-[10px] text-charcoal-400">+ pasivos</span>
          )}
          {!(it.active_languages?.length || it.passive_languages?.length) && it.languages && (
            <span className="text-[10px] text-charcoal-400 truncate">{it.languages}</span>
          )}
        </div>
        {(() => {
          const specs = Array.isArray(it.specialty)
            ? it.specialty
            : (it.specialty ? String(it.specialty).split(',') : []);
          const clean = specs.map((s) => String(s).trim()).filter(Boolean);
          if (!clean.length) return null;
          const shown = clean.slice(0, 4);
          const extra = clean.length - shown.length;
          return (
            <div className="flex flex-wrap items-center gap-1">
              <Briefcase size={10} className="text-charcoal-400 flex-shrink-0" />
              {shown.map((s) => (
                <span key={s} title={s} className="inline-block max-w-[8rem] truncate text-[10px] font-medium bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded border border-violet-100">{s}</span>
              ))}
              {extra > 0 && <span className="text-[10px] font-medium text-violet-600">+{extra}</span>}
            </div>
          );
        })()}
      </div>
      {/* Action bar */}
      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-1">
        <button
          onClick={onToggle}
          data-testid={`admin-interpreter-toggle-${it.slug}`}
          className={`p-1.5 rounded-lg transition-colors ${it.is_active ? 'text-cmic-600 hover:bg-cmic-100' : 'text-charcoal-400 hover:bg-gray-200'}`}
          title={it.is_active ? 'Ocultar' : 'Mostrar'}
        >
          {it.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
        </button>
        <div className="flex items-center gap-1">
          {!it.has_account && (it.email || '').trim() && !reorderMode && !inviteMode && (
            <button
              onClick={onInvite}
              data-testid={`admin-interpreter-invite-${it.slug}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 px-2.5 py-1.5 rounded-lg transition-colors"
              title={`Enviar invitación a ${it.email}`}
            >
              <Send size={12} /> Invitar
            </button>
          )}
          <button
            onClick={onEdit}
            data-testid={`admin-interpreter-edit-${it.slug}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-cmic-700 hover:bg-cmic-100 px-2.5 py-1.5 rounded-lg transition-colors"
          >
            <Pencil size={12} /> Editar
          </button>
          <button
            onClick={onRemove}
            data-testid={`admin-interpreter-delete-${it.slug}`}
            className="p-1.5 rounded-lg text-charcoal-400 hover:bg-red-50 hover:text-red-600 transition-colors"
            title="Eliminar"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TableView: dense list (toggle option)
// ───────────────────────────────────────────────────────────────────────────
function TableView({ items, onEdit, onRemove, onToggle, onInvite }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left px-4 py-3 font-medium text-charcoal-600 w-14"></th>
            <th className="text-left px-4 py-3 font-medium text-charcoal-600">Nombre</th>
            <th className="text-left px-4 py-3 font-medium text-charcoal-600">Idiomas</th>
            <th className="text-left px-4 py-3 font-medium text-charcoal-600">Ciudad</th>
            <th className="text-left px-4 py-3 font-medium text-charcoal-600">Email</th>
            <th className="text-center px-4 py-3 font-medium text-charcoal-600">Visible</th>
            <th className="w-32 px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((it) => {
            const initials = (it.full_name || '').split(' ').slice(0, 2).map((n) => n.charAt(0)).join('');
            return (
              <tr key={it.id} data-testid={`admin-interpreter-row-${it.slug}`} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  {it.photo_url ? (
                    <img src={absoluteUrl(it.photo_url)} alt="" className="w-9 h-9 rounded-lg object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cmic-500 to-cmic-700 text-white flex items-center justify-center text-[11px] font-bold">{initials}</div>
                  )}
                </td>
                <td className="px-4 py-3 font-medium text-charcoal-800">
                  <div className="flex items-center gap-2">
                    <span>{it.full_name}</span>
                    {it.has_account ? (
                      <span data-testid={`admin-interpreter-account-${it.slug}`} className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200" title="Tiene cuenta de usuario">
                        <UserCheck size={11} /> Con cuenta
                      </span>
                    ) : (
                      <span data-testid={`admin-interpreter-account-${it.slug}`} className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-gray-50 text-charcoal-400 border-gray-200" title="Aún no tiene cuenta de usuario">
                        <UserX size={11} /> Sin cuenta
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-charcoal-500">
                  <div className="flex flex-wrap gap-1 max-w-[260px]">
                    {(it.active_languages || []).map((l) => (
                      <span key={`a-${l}`} className="inline-block text-[10px] font-medium bg-cmic-50 text-cmic-700 px-1.5 py-0.5 rounded">{l}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-charcoal-500">{it.city}</td>
                <td className="px-4 py-3 text-charcoal-500 truncate max-w-[180px]"><span className="text-xs">{it.email}</span></td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => onToggle(it)}
                    data-testid={`admin-interpreter-toggle-${it.slug}`}
                    className={`p-1.5 rounded-lg transition-colors ${it.is_active ? 'text-cmic-600 hover:bg-cmic-50' : 'text-charcoal-400 hover:bg-gray-100'}`}
                  >
                    {it.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 justify-end">
                    {!it.has_account && (it.email || '').trim() && (
                      <button onClick={() => onInvite(it)} data-testid={`admin-interpreter-invite-${it.slug}`} className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50" title={`Enviar invitación a ${it.email}`}><Send size={15} /></button>
                    )}
                    <button onClick={() => onEdit(it)} data-testid={`admin-interpreter-edit-${it.slug}`} className="p-2 rounded-lg text-charcoal-500 hover:bg-cmic-50 hover:text-cmic-600"><Pencil size={15} /></button>
                    <button onClick={() => onRemove(it.slug)} data-testid={`admin-interpreter-delete-${it.slug}`} className="p-2 rounded-lg text-charcoal-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// EditorModal: section-based editor with LIVE PREVIEW
// ───────────────────────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'identity', label: 'Identidad y foto', icon: Camera },
  { id: 'contact', label: 'Contacto', icon: Phone },
  { id: 'languages', label: 'Idiomas', icon: Languages },
  { id: 'work', label: 'Especialidades y bio', icon: Briefcase },
  { id: 'membership', label: 'Membresía', icon: CalendarIcon },
];

function EditorModal({ editing, form, setForm, save, close, saving, error, catalogs, addToCatalog }) {
  const [activeSection, setActiveSection] = useState('identity');

  // Close the editor with the ESC key (previously you had to click ✕).
  // If a chip-select popover is open, let it consume the ESC first.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[data-testid$="-popover"]')) return;
      close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  // Live completion score (0-100) — gamification touch.
  const score = useMemo(() => {
    const checks = [
      !!form.full_name,
      !!form.photo_url,
      !!form.city,
      !!form.email,
      !!form.phone,
      !!form.linkedin_url,
      (form.active_languages || []).length > 0,
      (form.passive_languages || []).length > 0,
      Array.isArray(form.specialty) ? form.specialty.length > 0 : !!form.specialty,
      !!form.bio,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [form]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-4" onClick={close}>
      <div
        className="bg-white sm:rounded-2xl shadow-2xl w-full sm:max-w-6xl h-full sm:max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-interpreter-editor-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-cmic-50 to-white">
          <div>
            <h2 className="font-bold text-charcoal-800 text-lg">
              {editing === 'new' ? 'Nuevo intérprete' : form.full_name || 'Editar intérprete'}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 max-w-[120px] h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                  style={{ width: `${score}%` }}
                />
              </div>
              <span className="text-[11px] font-semibold text-charcoal-600">{score}% completo</span>
            </div>
          </div>
          <button onClick={close} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100"><X size={20} /></button>
        </div>

        {/* Body: 3-column on desktop (nav + form + preview) */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
          {/* Section nav */}
          <nav className="lg:w-56 lg:border-r border-gray-200 bg-gray-50/50 p-3 overflow-x-auto lg:overflow-y-auto flex lg:flex-col gap-1 flex-shrink-0">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  data-testid={`admin-interpreter-section-${s.id}`}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-white text-cmic-700 shadow-sm border border-gray-200'
                      : 'text-charcoal-600 hover:bg-white hover:text-charcoal-800'
                  }`}
                >
                  <Icon size={15} />
                  {s.label}
                </button>
              );
            })}
          </nav>

          {/* Form area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5 min-w-0">
            {activeSection === 'identity' && <SectionIdentity form={form} setForm={setForm} />}
            {activeSection === 'contact' && <SectionContact form={form} setForm={setForm} />}
            {activeSection === 'languages' && <SectionLanguages form={form} setForm={setForm} catalogs={catalogs} addToCatalog={addToCatalog} />}
            {activeSection === 'work' && <SectionWork form={form} setForm={setForm} catalogs={catalogs} addToCatalog={addToCatalog} />}
            {activeSection === 'membership' && <SectionMembership form={form} setForm={setForm} />}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 flex items-start gap-2" data-testid="admin-interpreter-error">
                <AlertCircle size={15} className="flex-shrink-0 mt-0.5" /> <span>{error}</span>
              </div>
            )}
          </div>

          {/* Live preview (desktop only) — full public-profile preview */}
          <div className="hidden xl:flex xl:w-96 border-l border-gray-200 bg-gradient-to-b from-gray-50 to-white p-6 flex-col flex-shrink-0 overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 text-xs font-semibold text-charcoal-500 uppercase tracking-wider">
              <Star size={12} /> Vista previa
            </div>
            <InterpreterProfilePreview form={form} label="" />
            <p className="text-[10px] text-charcoal-400 mt-3 text-center">Así se verá tu perfil público completo.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm text-charcoal-700">
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="rounded text-cmic-500 focus:ring-cmic-500"
              data-testid="admin-interpreter-field-is_active"
            />
            <span className="flex items-center gap-1.5"><Eye size={13} /> Visible en directorio</span>
          </label>
          <div className="flex items-center gap-2">
            <button onClick={close} className="px-4 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-interpreter-save-btn"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cmic-500 to-cmic-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:shadow-md hover:shadow-cmic-500/30 disabled:opacity-60 transition-all"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Editor sections
// ───────────────────────────────────────────────────────────────────────────
function SectionIdentity({ form, setForm }) {
  const ap = (form.apellido_paterno || '').trim();
  const am = (form.apellido_materno || '').trim();
  const nom = (form.nombres || '').trim();
  const derivedFullName = [nom, ap, am].filter(Boolean).join(' ').trim();
  return (
    <>
      <SectionHeader title="Identidad y foto" subtitle="Los apellidos y nombre del intérprete (orden mexicano)." icon={Camera} />
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
          label="Apellido paterno"
          name="apellido_paterno"
          form={form}
          setForm={setForm}
          placeholder="Ej. Lasso de la Vega"
          required
        />
        <Field
          label="Apellido materno"
          name="apellido_materno"
          form={form}
          setForm={setForm}
          placeholder="Ej. Márquez (puede ir vacío)"
        />
        <Field
          label="Nombre(s)"
          name="nombres"
          form={form}
          setForm={setForm}
          placeholder="Ej. María Elena"
          required
        />
      </div>
      <div className="bg-cmic-50/40 border border-cmic-100 rounded-lg px-4 py-3">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-cmic-600 mb-1">
          Vista previa del nombre completo
        </p>
        <p
          className="font-semibold text-charcoal-800 text-base"
          data-testid="admin-interpreter-derived-full-name"
        >
          {derivedFullName || form.full_name || '—'}
        </p>
        <p className="text-[10px] text-charcoal-400 mt-1">
          Este nombre se genera automáticamente a partir de los 3 campos y se usa
          en el directorio público. El ordenamiento alfabético siempre toma como
          base el apellido paterno.
        </p>
      </div>
      <details className="text-xs text-charcoal-500">
        <summary className="cursor-pointer hover:text-charcoal-700">
          Editar manualmente el nombre completo (legado)
        </summary>
        <div className="mt-2">
          <Field
            label="Nombre completo (manual)"
            name="full_name"
            form={form}
            setForm={setForm}
            placeholder="Solo si necesitas un formato especial"
          />
          <p className="text-[10px] text-charcoal-400 mt-1">
            Cuando los 3 campos están llenos, este campo es ignorado y se
            sobrescribe al guardar.
          </p>
        </div>
      </details>
      <div>
        <label className="block text-xs font-semibold text-charcoal-600 mb-2">Foto de perfil</label>
        <ImageUploader
          value={form.photo_url || ''}
          onChange={(url) => setForm({ ...form, photo_url: url })}
          testid="admin-interpreter-photo-uploader"
          shape="circle"
          cropAspect={1}
        />
        <p className="text-[10px] text-charcoal-400 mt-1.5">
          JPG/PNG/WebP, máximo 5 MB. Al subir te abriré un recorte cuadrado — ajusta zoom y centra la cara, queda perfecta para el directorio.
        </p>
      </div>
    </>
  );
}

function SectionContact({ form, setForm }) {
  return (
    <>
      <SectionHeader title="Contacto" subtitle="Información de cómo los clientes pueden contactarlo." icon={Phone} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Ciudad / País" name="city" form={form} setForm={setForm} placeholder="Ej. Ciudad de México, México" icon={MapPin} />
        <Field label="Email principal" name="email" form={form} setForm={setForm} type="email" placeholder="contacto@ejemplo.com" icon={Mail} />
        <Field label="Teléfono / WhatsApp principal" name="phone" form={form} setForm={setForm} placeholder="+52 55 1234 5678" icon={Phone} />
        <Field label="LinkedIn" name="linkedin_url" form={form} setForm={setForm} placeholder="https://linkedin.com/in/..." icon={Linkedin} />
        <Field label="Página web" name="website" form={form} setForm={setForm} placeholder="https://tu-sitio.com" icon={Globe} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div className="bg-cmic-50/30 border border-cmic-100 rounded-xl p-3">
          <label className="block text-xs font-semibold text-cmic-700 mb-2 uppercase tracking-wide flex items-center gap-1.5"><Mail size={12} /> Correos adicionales</label>
          <ChipInput value={form.additional_emails || []} onChange={(v) => setForm({ ...form, additional_emails: v })} placeholder="otro@correo.com (Enter)" testid="admin-additional-emails" />
          <p className="text-[10px] text-charcoal-500 mt-1.5">El principal recibe los mensajes de "Contactar intérprete".</p>
        </div>
        <div className="bg-cmic-50/30 border border-cmic-100 rounded-xl p-3">
          <label className="block text-xs font-semibold text-cmic-700 mb-2 uppercase tracking-wide flex items-center gap-1.5"><Phone size={12} /> Teléfonos adicionales</label>
          <ChipInput value={form.additional_phones || []} onChange={(v) => setForm({ ...form, additional_phones: v })} placeholder="+52 … (Enter)" testid="admin-additional-phones" />
        </div>
      </div>
    </>
  );
}

function SectionLanguages({ form, setForm, catalogs, addToCatalog }) {
  return (
    <>
      <SectionHeader title="Idiomas" subtitle="Configura las combinaciones lingüísticas. Activos = interpreta HACIA ese idioma; pasivos = entiende DESDE." icon={Languages} />
      <div className="bg-cmic-50/30 border border-cmic-100 rounded-xl p-4">
        <label className="block text-xs font-semibold text-cmic-700 mb-2 uppercase tracking-wide">Idiomas activos (HACIA)</label>
        <ChipMultiSelect
          value={form.active_languages || []}
          onChange={(v) => setForm({ ...form, active_languages: v })}
          catalog={catalogs?.languages || []}
          onCatalogAdd={(val) => addToCatalog('languages', val)}
          placeholder="Selecciona o crea un idioma…"
          testid="admin-interpreter-active-languages"
          accent="cmic"
          compactLimit={12}
        />
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <label className="block text-xs font-semibold text-charcoal-600 mb-2 uppercase tracking-wide">Idiomas pasivos (DESDE)</label>
        <ChipMultiSelect
          value={form.passive_languages || []}
          onChange={(v) => setForm({ ...form, passive_languages: v })}
          catalog={catalogs?.languages || []}
          onCatalogAdd={(val) => addToCatalog('languages', val)}
          placeholder="Selecciona o crea un idioma…"
          testid="admin-interpreter-passive-languages"
          accent="amber"
          compactLimit={12}
        />
      </div>
      <CombinationsEditor form={form} setForm={setForm} />
      <Field label="Resumen de idiomas (legado)" name="languages" form={form} setForm={setForm} placeholder="Auto si dejas vacío" />

      <div className={`rounded-xl border p-4 flex items-start gap-3 ${form.data_verified ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <input
          type="checkbox"
          id="data_verified_chk"
          checked={!!form.data_verified}
          onChange={(e) => setForm({ ...form, data_verified: e.target.checked })}
          data-testid="admin-interpreter-data-verified"
          className="mt-0.5 w-4 h-4 accent-emerald-600"
        />
        <label htmlFor="data_verified_chk" className="cursor-pointer flex-1">
          <div className={`text-sm font-semibold ${form.data_verified ? 'text-emerald-800' : 'text-amber-900'}`}>
            {form.data_verified ? '✅ Datos verificados' : '⚠️ Datos sin verificar'}
          </div>
          <div className="text-xs text-charcoal-600 mt-0.5">
            Marca esto cuando hayas confirmado los idiomas y especialidades contra una fuente oficial.
            Sólo se muestra en el admin (lista de tarjetas), no es visible para el público.
          </div>
        </label>
      </div>
    </>
  );
}

// Visual editor for the directional language combinations shown on the public
// profile. Pairs are auto-derived from active/passive languages; the admin can
// switch OFF any pair the interpreter does not actually offer (e.g. understands
// Portuguese but doesn't interpret Portuguese→French). Disabled pairs are
// stored in `excluded_language_pairs`.
function CombinationsEditor({ form, setForm }) {
  const pairs = deriveLanguagePairs(form.active_languages, form.passive_languages);
  const excluded = new Set(form.excluded_language_pairs || []);

  function toggle(from, to) {
    const k = pairKey(from, to);
    const next = new Set(excluded);
    if (next.has(k)) next.delete(k); else next.add(k);
    setForm({ ...form, excluded_language_pairs: Array.from(next) });
  }
  function enableAll() {
    // Keep only excluded keys NOT in the current derived set (none here).
    setForm({ ...form, excluded_language_pairs: [] });
  }

  const enabledCount = pairs.filter(([f, t]) => !excluded.has(pairKey(f, t))).length;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="combinations-editor">
      <div className="flex items-start justify-between gap-2 mb-2">
        <label className="block text-xs font-semibold text-charcoal-700 uppercase tracking-wide flex items-center gap-1.5">
          <Languages size={12} className="text-cmic-600" /> Combinaciones lingüísticas
        </label>
        {pairs.length > 0 && (
          <span className="text-[11px] text-charcoal-500">
            {enabledCount} de {pairs.length} activas
          </span>
        )}
      </div>
      {pairs.length === 0 ? (
        <p className="text-xs text-charcoal-400 italic">
          Agrega idiomas activos y/o pasivos arriba para generar combinaciones.
        </p>
      ) : (
        <>
          <p className="text-[11px] text-charcoal-500 mb-3 leading-relaxed">
            Se generan automáticamente desde los idiomas. Desactiva las que el intérprete
            <strong> no</strong> ofrece (ej. entiende portugués pero no interpreta de portugués a francés).
            Las desactivadas no aparecen en el perfil público.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pairs.map(([from, to]) => {
              const k = pairKey(from, to);
              const off = excluded.has(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggle(from, to)}
                  data-testid={`combo-toggle-${k}`}
                  title={off ? 'Clic para activar' : 'Clic para desactivar'}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                    off
                      ? 'bg-gray-50 border-gray-200 text-charcoal-400 line-through opacity-70'
                      : 'bg-gradient-to-br from-white via-cmic-50/40 to-white border-cmic-200 text-charcoal-800 hover:border-cmic-300'
                  }`}
                >
                  <span className={`flex-shrink-0 w-2 h-2 rounded-full ${off ? 'bg-gray-300' : 'bg-cmic-500'}`} />
                  <span className="truncate">{from}</span>
                  <ArrowRight size={13} className={off ? 'text-gray-300' : 'text-cmic-500'} />
                  <span className="truncate">{to}</span>
                </button>
              );
            })}
          </div>
          {excluded.size > 0 && (
            <button
              type="button"
              onClick={enableAll}
              data-testid="combinations-enable-all"
              className="mt-3 text-[11px] font-medium text-cmic-600 hover:text-cmic-700 underline"
            >
              Activar todas
            </button>
          )}
        </>
      )}
    </div>
  );
}


function SectionWork({ form, setForm, catalogs, addToCatalog }) {
  return (
    <>
      <SectionHeader title="Especialidades y biografía" subtitle="Detalla las áreas de experiencia y trayectoria profesional." icon={Briefcase} />
      <div className="bg-cmic-50/30 border border-cmic-100 rounded-xl p-4">
        <label className="block text-xs font-semibold text-cmic-700 mb-2 uppercase tracking-wide flex items-center gap-1.5">
          <Briefcase size={12} /> Especialidades
        </label>
        <ChipMultiSelect
          value={Array.isArray(form.specialty) ? form.specialty : (form.specialty ? String(form.specialty).split(',').map((s) => s.trim()).filter(Boolean) : [])}
          onChange={(v) => setForm({ ...form, specialty: v })}
          catalog={catalogs?.specialties || []}
          onCatalogAdd={(val) => addToCatalog('specialties', val)}
          placeholder="Selecciona o crea una especialidad…"
          testid="admin-interpreter-specialty"
          accent="cmic"
          compactLimit={12}
        />
        <p className="text-[10px] text-charcoal-500 mt-1.5">Elige de la lista o escribe para crear una nueva. Aparecen como etiquetas en el perfil público.</p>
      </div>
      <div className="bg-violet-50/40 border border-violet-100 rounded-xl p-4">
        <label className="block text-xs font-semibold text-violet-700 mb-2 uppercase tracking-wide flex items-center gap-1.5">
          <Mic size={12} /> Modalidades de interpretación
        </label>
        <ChipMultiSelect
          value={form.modalities || []}
          onChange={(v) => setForm({ ...form, modalities: v })}
          catalog={catalogs?.modalities || []}
          onCatalogAdd={(val) => addToCatalog('modalities', val)}
          placeholder="Selecciona o crea una modalidad…"
          testid="admin-interpreter-modalities"
          accent="amber"
          compactLimit={12}
        />
        <p className="text-[10px] text-charcoal-500 mt-1.5">Elige de la lista de modalidades o escribe para añadir una nueva.</p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-charcoal-600 mb-1.5">Semblanza</label>
        <RichTextEditor
          value={form.bio || ''}
          onChange={(html) => setForm({ ...form, bio: html })}
          testid="admin-interpreter-field-bio"
          placeholder="Trayectoria, experiencia, distinciones, certificaciones..."
          minHeight={200}
        />
        <p className="text-[10px] text-charcoal-400 mt-1">Usa la barra para negritas, cursiva, alineación (justificado), listas y enlaces. Se mostrará con ese formato en el perfil público.</p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-charcoal-600 mb-1.5">Certificaciones</label>
        <CertificationsField
          value={form.references_text || ''}
          onChange={(v) => setForm({ ...form, references_text: v })}
        />
        <p className="mt-1.5 text-[11px] text-charcoal-400">Una certificación por casilla. Se mostrarán numeradas (1, 2, 3…) en el perfil público.</p>
      </div>
    </>
  );
}

function SectionMembership({ form, setForm }) {
  const paidUntilDate = form.paid_until ? new Date(form.paid_until.slice(0, 10) + 'T12:00:00') : undefined;
  return (
    <>
      <SectionHeader title="Membresía" subtitle="Control interno de cuotas. No es visible públicamente." icon={CalendarIcon} />
      <div className="mb-4">
        <label className="block text-xs font-semibold text-charcoal-600 mb-1.5">Estatus de miembro</label>
        <select
          value={form.membership_status || 'active'}
          onChange={(e) => setForm({ ...form, membership_status: e.target.value })}
          data-testid="admin-interpreter-field-membership_status"
          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
        >
          <option value="active">Activo</option>
          <option value="senior">Senior</option>
          <option value="honorary">Honorario</option>
          <option value="associate">Asociado</option>
          <option value="candidate">Candidato</option>
          <option value="excluded">Excluido</option>
        </select>
        <p className="text-[10px] text-charcoal-400 mt-1">Define la categoría del miembro (Activo, Senior, etc.). También editable desde Usuarios y Cuotas.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-charcoal-600 mb-1.5">Cuota pagada hasta</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="admin-interpreter-field-paid_until"
                className="w-full inline-flex items-center justify-between rounded-xl border border-gray-200 px-4 py-2.5 text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 transition-all"
              >
                <span className={paidUntilDate ? 'text-charcoal-800' : 'text-charcoal-400'}>
                  {paidUntilDate ? paidUntilDate.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Sin fecha'}
                </span>
                <CalendarIcon size={14} className="text-charcoal-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={paidUntilDate}
                onSelect={(d) => {
                  if (!d) { setForm({ ...form, paid_until: '' }); return; }
                  const iso = d.toISOString().slice(0, 10);
                  setForm({ ...form, paid_until: iso });
                }}
                initialFocus
              />
              {paidUntilDate && (
                <div className="border-t border-gray-100 p-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, paid_until: '' })}
                    className="text-xs text-rose-600 hover:bg-rose-50 px-2 py-1 rounded font-medium"
                  >
                    Limpiar fecha
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <p className="text-[10px] text-charcoal-400 mt-1">Vacío = sin registro. Vencida = NO aparece en directorio público.</p>
        </div>
        <Field
          label="Notas internas"
          name="membership_notes"
          form={form}
          setForm={setForm}
          placeholder="Ej. Pago en efectivo, recibo #123"
        />
      </div>
    </>
  );
}

function SectionHeader({ title, subtitle, icon: Icon }) {
  return (
    <div className="border-b border-gray-100 pb-3 mb-1">
      <h3 className="font-bold text-charcoal-800 flex items-center gap-2">
        <Icon size={16} className="text-cmic-500" /> {title}
      </h3>
      {subtitle && <p className="text-xs text-charcoal-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function Field({ label, name, form, setForm, type = 'text', placeholder, required, icon: Icon }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-charcoal-600 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="relative">
        {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400 pointer-events-none" />}
        <input
          type={type}
          value={form[name] || ''}
          onChange={(e) => setForm({ ...form, [name]: e.target.value })}
          placeholder={placeholder}
          data-testid={`admin-interpreter-field-${name}`}
          className={`w-full rounded-xl border border-gray-200 ${Icon ? 'pl-9' : 'pl-4'} pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 transition-all`}
        />
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────


// Banner — perfiles incompletos.
//
// Fetches /admin/interpreters/incomplete-profiles on mount and shows a
// collapsible card listing interpreters with missing critical fields.
// Clicking a row opens that interpreter's edit modal so the admin can fill
// in the gaps without leaving the page.
function IncompleteProfilesBanner({ items, onSelect }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  async function ensureLoaded() {
    if (fetched) return;
    setFetched(true);
    setLoading(true);
    try {
      const { data: d } = await api.get('/admin/interpreters/incomplete-profiles');
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle() {
    await ensureLoaded();
    setOpen((v) => !v);
  }

  // If we haven't loaded yet, render a compact placeholder so the admin sees
  // the affordance to expand it. The actual count appears after they open it.
  if (!fetched) {
    return (
      <button
        type="button"
        onClick={handleToggle}
        data-testid="incomplete-banner-load"
        className="rounded-xl border border-amber-200 bg-amber-50/60 text-amber-900 text-sm px-4 py-3 w-full flex items-center justify-between hover:bg-amber-100/40 transition-colors"
      >
        <span className="flex items-center gap-2">
          <AlertCircle size={16} className="text-amber-700" />
          Revisar perfiles incompletos
        </span>
        <ChevronDown size={16} className="text-amber-700" />
      </button>
    );
  }

  if (loading && !data) {
    return <div className="rounded-xl border border-amber-100 bg-amber-50/40 text-amber-800 text-sm px-4 py-3" data-testid="incomplete-banner-loading">Analizando perfiles…</div>;
  }
  if (!data || data.incomplete_count === 0) {
    return (
      <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 text-emerald-800 text-sm px-4 py-3 flex items-center gap-2" data-testid="incomplete-banner-empty">
        <CheckCircle2 size={16} /> Todos los perfiles del directorio están completos.
      </div>
    );
  }

  const incomplete = data.incomplete;
  const pct = data.total_listed ? Math.round((incomplete.length / data.total_listed) * 100) : 0;

  function handleClick(row) {
    const item = (items || []).find((i) => i.slug === row.slug);
    if (item && onSelect) onSelect(item);
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 overflow-hidden" data-testid="incomplete-banner">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-amber-100/40 transition-colors"
      >        <div className="flex items-center gap-3 min-w-0">
          <AlertCircle size={18} className="text-amber-700 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {incomplete.length} {incomplete.length === 1 ? 'perfil incompleto' : 'perfiles incompletos'} ({pct}% del directorio)
            </p>
            <p className="text-xs text-amber-700/80">
              Hay miembros con campos críticos vacíos: foto, idiomas, especialidades, etc.
            </p>
          </div>
        </div>
        <ChevronDown size={16} className={`text-amber-700 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-amber-200/70 max-h-96 overflow-y-auto bg-white/50">
          {incomplete.map((row) => (
            <button
              key={row.slug}
              type="button"
              onClick={() => handleClick(row)}
              data-testid={`incomplete-row-${row.slug}`}
              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-amber-100/40 border-b border-amber-100/50 last:border-b-0 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-charcoal-800 truncate">
                  {row.member_number ? <span className="text-charcoal-400 mr-1.5">#{row.member_number}</span> : null}
                  {row.full_name}
                </p>
                <div className="flex items-center flex-wrap gap-1 mt-1">
                  {row.missing_fields.map((f) => (
                    <span
                      key={f}
                      className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide bg-amber-200/60 text-amber-800 rounded px-1.5 py-0.5"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-xs text-amber-700 font-semibold flex-shrink-0">
                {row.missing_count} {row.missing_count === 1 ? 'campo' : 'campos'}
                <Pencil size={11} className="inline ml-1.5" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/** Bulk-invite UI for sending account-creation emails to interpreters.
 *  Three ways to pick recipients:
 *   1. Atajos por tipo (chips) — preselect everyone of that membership_status.
 *   2. Selector individual (collapsible list with checkboxes + filter pills).
 *   3. Card selection mode — toggle, then click cards to add/remove.
 *  All three share the same `selected` set (lifted to the page).
 */
function BulkInvitePanel({ items, onDone, inviteMode, setInviteMode, selected, setSelected }) {
  const [open, setOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const eligibleAll = useMemo(
    () => (items || []).filter((i) => (i.email || '').trim() && !i.user_id),
    [items],
  );

  const statusCounts = useMemo(() => {
    const c = {};
    for (const it of eligibleAll) {
      const k = it.membership_status || 'sin_status';
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [eligibleAll]);

  const STATUS_LABELS = {
    active: 'Activos', senior: 'Senior', honorary: 'Honorarios',
    associate: 'Asociados', candidate: 'Candidatos', excluded: 'Excluidos',
    sin_status: 'Sin status',
  };

  const eligible = useMemo(() => {
    if (filterStatus === 'all') return eligibleAll;
    return eligibleAll.filter((i) => (i.membership_status || 'sin_status') === filterStatus);
  }, [eligibleAll, filterStatus]);

  const totalWithoutUser = (items || []).filter((i) => !i.user_id).length;

  function toggle(slug) {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    setSelected(next);
  }

  function toggleAllVisible() {
    const visibleSlugs = eligible.map((i) => i.slug);
    const allSelected = visibleSlugs.every((s) => selected.has(s));
    const next = new Set(selected);
    if (allSelected) visibleSlugs.forEach((s) => next.delete(s));
    else visibleSlugs.forEach((s) => next.add(s));
    setSelected(next);
  }

  function selectStatusGroup(statusKey) {
    const next = new Set(selected);
    for (const it of eligibleAll) {
      if ((it.membership_status || 'sin_status') === statusKey) next.add(it.slug);
    }
    setSelected(next);
    setFilterStatus(statusKey);
  }

  function clearSelection() { setSelected(new Set()); }

  async function send() {
    if (selected.size === 0) return;
    if (!window.confirm(`Se enviarán ${selected.size} invitaciones. ¿Continuar?`)) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post('/admin/interpreters/bulk-invite', {
        slugs: Array.from(selected),
        invite_all_with_email: false,
      });
      setResult(data);
      clearSelection();
      setInviteMode(false);
      onDone?.();
    } catch (e) {
      setResult({ summary: { sent: 0, skipped: 0, failed: 1 }, results: [{ status: 'email_failed', error: formatApiError(e.response?.data?.detail) }] });
    } finally {
      setBusy(false);
    }
  }

  async function sendAll() {
    if (!window.confirm(`Se enviará una invitación a TODOS los intérpretes con correo y sin cuenta (~${eligibleAll.length} correos). ¿Continuar?`)) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post('/admin/interpreters/bulk-invite', {
        slugs: [],
        invite_all_with_email: true,
      });
      setResult(data);
      clearSelection();
      onDone?.();
    } catch (e) {
      setResult({ summary: { sent: 0, skipped: 0, failed: 1 }, results: [{ status: 'email_failed', error: formatApiError(e.response?.data?.detail) }] });
    } finally {
      setBusy(false);
    }
  }

  if (totalWithoutUser === 0) return null;

  return (
    <>
    <div className="bg-blue-50/50 border border-blue-200 rounded-2xl p-4" data-testid="bulk-invite-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-[280px]">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
            <UserPlus size={18} className="text-blue-700" />
          </div>
          <div>
            <h3 className="font-semibold text-charcoal-800 text-sm">Invitar a intérpretes a crear su cuenta</h3>
            <p className="text-xs text-charcoal-600 mt-1">
              {eligibleAll.length} con correo y sin cuenta. Tres formas de elegir: atajos por tipo, lista individual, o seleccionar cards directo.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setInviteMode(!inviteMode); setOpen(false); }}
            data-testid="bulk-invite-card-mode-btn"
            className={`text-xs font-medium px-3 py-2 rounded-lg inline-flex items-center gap-1.5 ${inviteMode ? 'bg-blue-600 text-white' : 'bg-white border border-blue-300 text-blue-700 hover:bg-blue-50'}`}
          >
            <CheckCircle2 size={13} />
            {inviteMode ? 'Selección en cards: ACTIVA' : 'Seleccionar en las cards'}
          </button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="bulk-invite-open-btn"
            className="text-xs font-medium text-blue-700 hover:text-blue-800 px-3 py-2 rounded-lg hover:bg-blue-100"
          >
            {open ? 'Cerrar lista' : 'Lista con filtros'}
          </button>
          <button
            onClick={sendAll}
            disabled={busy || eligibleAll.length === 0}
            data-testid="bulk-invite-all-btn"
            className="text-xs font-medium bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Send size={13} />{busy ? 'Enviando…' : `TODOS (${eligibleAll.length})`}
          </button>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-blue-200">
        <p className="text-[11px] font-semibold text-charcoal-600 mb-2">Atajos: agrega un grupo entero a tu selección</p>
        <div className="flex flex-wrap gap-2" data-testid="bulk-invite-status-shortcuts">
          {Object.entries(statusCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([key, count]) => (
              <button
                key={key}
                onClick={() => selectStatusGroup(key)}
                disabled={busy}
                data-testid={`bulk-invite-status-${key}`}
                className="inline-flex items-center gap-1.5 bg-white border border-blue-200 hover:border-blue-400 hover:bg-blue-50 text-charcoal-700 text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
                title={`Agregar a la selección los ${count} ${STATUS_LABELS[key] || key} elegibles`}
              >
                <span>+ {STATUS_LABELS[key] || key}</span>
                <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{count}</span>
              </button>
            ))}
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-blue-200">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[11px] text-charcoal-500">Filtrar lista:</span>
            <button
              onClick={() => setFilterStatus('all')}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${filterStatus === 'all' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-charcoal-600'}`}
            >
              Todos ({eligibleAll.length})
            </button>
            {Object.entries(statusCounts).map(([key, count]) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${filterStatus === key ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-charcoal-600'}`}
              >
                {STATUS_LABELS[key] || key} ({count})
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-2">
            <button onClick={toggleAllVisible} className="text-xs font-medium text-blue-700 hover:underline">
              {eligible.every((i) => selected.has(i.slug)) && eligible.length > 0
                ? `Deseleccionar los ${eligible.length} visibles`
                : `Seleccionar los ${eligible.length} visibles`}
            </button>
            <button
              onClick={send}
              disabled={busy || selected.size === 0}
              className="text-xs font-medium bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Send size={12} className="inline mr-1" />{busy ? 'Enviando…' : `Enviar a ${selected.size}`}
            </button>
          </div>

          <div className="max-h-72 overflow-y-auto bg-white rounded-lg border border-blue-100 divide-y divide-gray-100">
            {eligible.map((it) => (
              <label key={it.slug} className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm">
                <input type="checkbox" checked={selected.has(it.slug)} onChange={() => toggle(it.slug)} />
                <span className="flex-1 truncate text-charcoal-800">{it.full_name}</span>
                {it.membership_status && (
                  <span className="text-[10px] font-medium uppercase tracking-wider bg-gray-100 text-charcoal-500 px-2 py-0.5 rounded">
                    {STATUS_LABELS[it.membership_status] || it.membership_status}
                  </span>
                )}
                <span className="text-xs text-charcoal-500 truncate max-w-[200px]">{it.email}</span>
              </label>
            ))}
            {eligible.length === 0 && (
              <p className="text-center py-4 text-charcoal-400 text-xs">No hay intérpretes elegibles con este filtro.</p>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-4 pt-4 border-t border-blue-200" data-testid="bulk-invite-result">
          <p className="text-xs font-semibold text-charcoal-700 mb-2">Resultado:</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />{result.summary.sent} enviadas
            </span>
            <span className="inline-flex items-center gap-1 text-amber-700">
              <span className="w-2 h-2 rounded-full bg-amber-500" />{result.summary.skipped} omitidas
            </span>
            <span className="inline-flex items-center gap-1 text-rose-700">
              <span className="w-2 h-2 rounded-full bg-rose-500" />{result.summary.failed} fallaron
            </span>
          </div>
          <p className="text-[11px] text-charcoal-500 mt-2">
            En modo prueba todas las invitaciones se redirigen a la casilla de pruebas.
          </p>
        </div>
      )}
    </div>

    {/* Floating selection bar — visible whenever there is at least 1 selected */}
    {selected.size > 0 && (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-blue-600 text-white rounded-full shadow-2xl px-5 py-3 flex items-center gap-4 animate-fade-in" data-testid="invite-selection-floating-bar">
        <span className="text-sm font-semibold">{selected.size} seleccionado{selected.size === 1 ? '' : 's'}</span>
        <button onClick={clearSelection} className="text-xs underline opacity-80 hover:opacity-100" data-testid="invite-selection-clear">
          limpiar
        </button>
        <button
          onClick={send}
          disabled={busy}
          data-testid="invite-selection-send"
          className="bg-white text-blue-700 text-xs font-bold px-4 py-1.5 rounded-full hover:bg-blue-50 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <Send size={12} />{busy ? 'Enviando…' : 'Enviar invitaciones'}
        </button>
      </div>
    )}
    </>
  );
}
